import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  GlobalStyles,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import {
  ReportControlBar,
  ReportPageHeading,
  ReportSectionCard,
} from "src/shared/components/report-layout";
import { authState } from "src/store/auth";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import { useGetBalanceSheetReportData } from "src/api/reports/balance";
import TrialBalanceTable from "./table";
import { DatePicker } from "@mui/x-date-pickers";

type ItemDetailReportFilters = {
  date: Date;
  branchId: string;
  period: string;
};

const BalanceSheetReportPage = () => {
  const { t } = useTranslation("reports");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useRecoilValue(authState);
  const companyName = auth.user?.company?.name;

  const formValues = useMemo((): ItemDetailReportFilters => {
    return {
      date: searchParams.get("date")
        ? moment(searchParams.get("date")).toDate()
        : moment().toDate(),
      branchId: searchParams.get("branch_id") || "",
      period: searchParams.get("period") || "0",
    };
  }, [searchParams]);

  const { control } = useForm<ItemDetailReportFilters>({
    values: formValues,
  });

  const { date, branchId: branchIdParam, period } = formValues;

  const branchIdNum = branchIdParam ? Number(branchIdParam) : NaN;
  const branchFilter = Number.isFinite(branchIdNum) ? [branchIdNum] : [];

  const {
    data: reportResponse,
    isFetching,
    refetch: refetchReport,
  } = useGetBalanceSheetReportData({
    branches: branchFilter,
    date: moment(date).format("YYYY-MM-DD"),
    period: period,
  });

  const rows = useMemo(
    () => reportResponse?.data?.rows ?? [],
    [reportResponse],
  );

  const syncPeriodToUrl = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("period", id);
    else next.delete("period");
    setSearchParams(next);
  };

  const syncDateToUrl = (date: string) => {
    const next = new URLSearchParams(searchParams);
    if (date) next.set("date", date);
    else next.delete("date");
    setSearchParams(next);
  };

  const periodLabel = useMemo(() => {
    return `${generalT("to")}: ${moment(date).format("D MMM YYYY")}`;
  }, [date]);

  const handleDownload = () => {};

  const handlePrint = () => {
    window.print();
  };

  return (
    <Box p={{ xs: 2, sm: "12px 32px 32px" }} maxWidth="100%">
      <GlobalStyles
        styles={{
          "@media print": {
            "body *": { visibility: "hidden" },
            "#item-detail-print-root, #item-detail-print-root *": {
              visibility: "visible",
            },
            "#item-detail-print-root": {
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
            },
          },
        }}
      />

      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        color="#101828"
        mb={1.5}
        sx={{ "@media print": { display: "none" } }}
      >
        <Typography
          fontSize="0.9rem"
          color="#475467"
          onClick={() => navigate("/reports")}
          sx={{ cursor: "pointer" }}
        >
          {t("thirdEyeReport")}
        </Typography>
        <img src={RightArrow} alt="" style={{ width: 12, height: 12 }} />
        <Box borderRadius="4px" bgcolor="#EAECF0" px={1} py={0.5}>
          <Typography color="#344054" fontWeight={600} fontSize="0.85rem">
            {t("balanceSheet")}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ "@media print": { display: "none" } }} mb={2}>
        <ReportControlBar
          left={
            <>
              <Box sx={{ minWidth: 200 }}>
                <Controller
                  name="date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          label: generalT("to"),
                        },
                      }}
                      format={"dd/MM/yyyy"}
                      onChange={(value) => {
                        field.onChange(value);
                        syncDateToUrl(moment(value).format("YYYY-MM-DD"));
                      }}
                    />
                  )}
                />
              </Box>

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="demo-simple-select-label">
                  {generalT("period")}
                </InputLabel>
                <Controller
                  name="period"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      key={field.value}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      label={generalT("period")}
                      onChange={(e) => {
                        const v = e.target.value as string;
                        field.onChange(v);
                        syncPeriodToUrl(v);
                      }}
                    >
                      <MenuItem value={0}>{t("0")}</MenuItem>
                      <MenuItem value={1}>{t("1")}</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
            </>
          }
          center={
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={() => void refetchReport()}
                disabled={isFetching}
                sx={{ height: 40, minWidth: 120 }}
              >
                {t("reportUpdate")}
              </Button>
            </>
          }
          right={
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<PrintIcon fontSize="small" />}
                onClick={handlePrint}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("print")}
              </Button>
              <LoadingButton
                variant="outlined"
                startIcon={<FileDownloadIcon fontSize="small" />}
                onClick={handleDownload}
                // loading={isDownloadingReport}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("export")}
              </LoadingButton>
            </>
          }
        />
      </Box>

      <Box id="item-detail-print-root">
        <ReportPageHeading
          title={t("balanceSheet")}
          subtitle={companyName}
          periodLabel={periodLabel}
        />

        <ReportSectionCard>
          <TrialBalanceTable
            data={rows}
            isLoading={isFetching}
            columns={reportResponse?.data?.columns}
          />
        </ReportSectionCard>
      </Box>
    </Box>
  );
};

export default BalanceSheetReportPage;
